import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";
import { useLanguage } from "@/context/LanguageContext";
import { getProduct, PRODUCTS, coverOf, SIZES, eur } from "@/lib/shop";

export default function Artist() {
  const { id } = useParams();
  const product = getProduct(id);
  const { t, lang } = useLanguage();

  if (!product || !product.artist) {
    return (
      <div className="bg-[#faf8f5] min-h-screen">
        <Nav solid />
        <div className="pt-40 text-center font-light text-[#111111]/70">
          {t("product.notFound")}{" "}
          <Link to="/artists" className="link-underline">{t("artist.allArtists")}</Link>
        </div>
      </div>
    );
  }

  const a = product.artist;
  const others = PRODUCTS.filter((p) => p.id !== id).slice(0, 4);
  const from = Math.min(...SIZES.map((s) => s.prices.unframed));

  return (
    <div className="bg-[#faf8f5] min-h-screen">
      <Nav solid />
      <main className="pt-20 md:pt-24 pb-24">
        {/* Hero — in-situ cover */}
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <Link
            to="/artists"
            className="inline-flex items-center gap-2 text-sm font-light text-[#111111]/70 hover:text-[#111111]"
            data-testid="artist-back"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> {t("artist.allArtists")}
          </Link>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
            <div className="lg:col-span-7">
              <div className="border gallery-line overflow-hidden bg-white">
                <img
                  src={coverOf(product)}
                  alt={`${product.tagline} — in situ`}
                  data-testid="artist-hero"
                  className="w-full aspect-[4/3] object-cover"
                />
              </div>
            </div>
            <div className="lg:col-span-5">
              <span className="text-xs uppercase tracking-[0.3em] text-[#a89f8e]">
                {a.movement[lang] || a.movement.en}
              </span>
              <h1 className="mt-4 font-display text-4xl md:text-6xl tracking-tight text-[#111111]">
                {a.name}
              </h1>
              <p className="mt-3 font-display italic text-xl text-[#a89f8e]">{a.years}</p>
              <p className="mt-6 text-base md:text-lg font-light leading-relaxed text-[#111111]/75" data-testid="artist-bio">
                {a.bio[lang] || a.bio.en}
              </p>
            </div>
          </div>
        </div>

        {/* Major works */}
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 mt-20 md:mt-28">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.3em] text-[#a89f8e]">
              01 — {t("artist.majorWorks")}
            </span>
            <ol className="mt-8 border-t gallery-line" data-testid="artist-works">
              {a.works.map((w, i) => (
                <li
                  key={w}
                  className="flex items-baseline gap-6 border-b gallery-line py-5"
                >
                  <span className="font-display italic text-lg text-[#a89f8e] w-8 shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-display text-xl md:text-2xl tracking-tight text-[#111111]">
                    {w}
                  </span>
                </li>
              ))}
            </ol>
          </Reveal>
        </div>

        {/* The concept tie-in — our work */}
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 mt-20 md:mt-28">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.3em] text-[#a89f8e]">
              02 — {t("artist.theX")}
            </span>
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
              <div className="lg:col-span-5">
                <p className="font-display italic text-2xl md:text-3xl leading-snug tracking-tight text-[#111111]" data-testid="artist-concept">
                  “{a.concept[lang] || a.concept.en}”
                </p>
                <p className="mt-6 text-sm font-light leading-relaxed text-[#111111]/60">
                  {t("artist.conceptNote")}
                </p>
                <Link
                  to={`/shop/${product.id}`}
                  data-testid="artist-view-print"
                  className="mt-8 inline-flex items-center gap-2 bg-[#111111] px-7 py-4 text-sm font-light tracking-wide text-[#faf8f5] transition-opacity duration-500 hover:opacity-85"
                >
                  {t("artist.viewPrint")} — {product.name}{" "}
                  <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
                </Link>
                <p className="mt-4 text-sm font-light text-[#111111]/60">
                  {t("shop.from")} {eur(from, lang)} — Noir / Bi-color
                </p>
              </div>
              <div className="lg:col-span-7">
                <Link to={`/shop/${product.id}`} className="block border gallery-line bg-white overflow-hidden group">
                  <img
                    src={product.prints[product.cardColor || "bicolor"]}
                    alt={product.tagline}
                    className="w-full aspect-[4/3] object-contain p-10 md:p-16 transition-transform duration-[1.2s] ease-out group-hover:scale-[1.02]"
                  />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Other artists */}
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 mt-24 md:mt-32">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.3em] text-[#a89f8e]">
              {t("artist.otherArtists")}
            </span>
            <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 border-t border-l gallery-line">
              {others.map((p) => (
                <Link
                  key={p.id}
                  to={`/artists/${p.id}`}
                  data-testid={`artist-other-${p.id}`}
                  className="group border-r border-b gallery-line"
                >
                  <div className="overflow-hidden bg-white">
                    <img
                      src={coverOf(p)}
                      alt={p.artist?.name || p.name}
                      loading="lazy"
                      className="aspect-[4/3] w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.03]"
                    />
                  </div>
                  <div className="px-5 py-4">
                    <h3 className="font-display italic text-lg tracking-tight text-[#111111]">
                      {p.artist?.name}
                    </h3>
                    <p className="mt-1 text-[11px] uppercase tracking-[0.18em] text-[#a89f8e]">
                      {p.artist?.years}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </Reveal>
        </div>
      </main>
      <Footer />
    </div>
  );
}
